/**
 * Client-side helpers for 360Worlds access + promotional free windows.
 * Server gate remains `can_view_worlds_tour`; these only drive UI.
 */

export type WorldsAccess = 'free' | 'subscription' | 'promotional';

export const WORLDS_PROMO_TITLE_SUGGESTIONS = [
    'New World Launch',
    'Summer Holidays',
    'World Environment Day',
    'Black Friday',
    'Christmas Special',
    'Back to School',
    'Teacher Appreciation Week',
    'Easter Holidays',
    'Halloween',
    'World Oceans Day',
    'Earth Day',
    'Black History Month',
    'British Science Week',
    'National Space Week',
    'Teacher Promotion',
    'School Trial Week',
] as const;

export function parseWorldsAccess(value: unknown): WorldsAccess {
    if (value === 'free' || value === 'promotional') return value;
    return 'subscription';
}

/** True when the world is free to everyone right now (permanent or active promo). */
export function isWorldCurrentlyFree(opts: {
    access: WorldsAccess;
    promoStartsAt?: string | null;
    promoEndsAt?: string | null;
    isPromoActive?: boolean | null;
    now?: Date;
}): boolean {
    if (opts.access === 'free') return true;
    if (opts.access !== 'promotional') return false;
    if (opts.isPromoActive === true) return true;
    if (opts.isPromoActive === false) return false;
    const starts = opts.promoStartsAt ? new Date(opts.promoStartsAt).getTime() : NaN;
    const ends = opts.promoEndsAt ? new Date(opts.promoEndsAt).getTime() : NaN;
    if (!Number.isFinite(starts) || !Number.isFinite(ends)) return false;
    const t = (opts.now ?? new Date()).getTime();
    return t >= starts && t < ends;
}

export function daysRemaining(endsAt: string | null | undefined, now = new Date()): number | null {
    if (!endsAt) return null;
    const ends = new Date(endsAt).getTime();
    if (!Number.isFinite(ends)) return null;
    const ms = ends - now.getTime();
    if (ms <= 0) return 0;
    return Math.ceil(ms / (24 * 60 * 60 * 1000));
}

export interface PromoCountdownParts {
    totalMs: number;
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
}

export function getPromoCountdownParts(
    endsAt: string | null | undefined,
    now = new Date(),
): PromoCountdownParts {
    if (!endsAt) {
        return { totalMs: 0, days: 0, hours: 0, minutes: 0, seconds: 0 };
    }
    const ends = new Date(endsAt).getTime();
    if (!Number.isFinite(ends)) {
        return { totalMs: 0, days: 0, hours: 0, minutes: 0, seconds: 0 };
    }
    const totalMs = Math.max(0, ends - now.getTime());
    const days = Math.floor(totalMs / (24 * 60 * 60 * 1000));
    const hours = Math.floor((totalMs % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
    const minutes = Math.floor((totalMs % (60 * 60 * 1000)) / (60 * 1000));
    const seconds = Math.floor((totalMs % (60 * 1000)) / 1000);
    return { totalMs, days, hours, minutes, seconds };
}

/**
 * Human countdown copy:
 * - ≥ 2 days: "12 days remaining"
 * - < 2 days: "Ends in 4 days 16 hours" / "Ends in 16 hours" / "Ends in 45 minutes"
 */
export function formatPromoCountdown(
    endsAt: string | null | undefined,
    now = new Date(),
): string | null {
    const parts = getPromoCountdownParts(endsAt, now);
    if (parts.totalMs <= 0) return null;

    if (parts.days >= 2) {
        return `${parts.days} days remaining`;
    }
    if (parts.days === 1) {
        return parts.hours > 0
            ? `Ends in 1 day ${parts.hours} hour${parts.hours === 1 ? '' : 's'}`
            : 'Ends in 1 day';
    }
    if (parts.hours > 0) {
        return `Ends in ${parts.hours} hour${parts.hours === 1 ? '' : 's'}${
            parts.minutes > 0 ? ` ${parts.minutes} minute${parts.minutes === 1 ? '' : 's'}` : ''
        }`;
    }
    if (parts.minutes > 0) {
        return `Ends in ${parts.minutes} minute${parts.minutes === 1 ? '' : 's'}`;
    }
    return 'Ends soon';
}

/** Format promo end for cards: "31 August" / "31 August 2027" when not this year. */
export function formatPromoEndDate(endsAt: string | null | undefined): string | null {
    if (!endsAt) return null;
    const d = new Date(endsAt);
    if (!Number.isFinite(d.getTime())) return null;
    const sameYear = d.getFullYear() === new Date().getFullYear();
    return d.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        ...(sameYear ? {} : { year: 'numeric' }),
    });
}

/** datetime-local value from ISO timestamptz (local timezone). */
export function toDatetimeLocalValue(iso: string | null | undefined): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (!Number.isFinite(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Parse datetime-local input to ISO string, or null if empty/invalid. */
export function fromDatetimeLocalValue(value: string): string | null {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const d = new Date(trimmed);
    if (!Number.isFinite(d.getTime())) return null;
    return d.toISOString();
}
